import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, Link, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import ApiService from '../../../../services/ApiService'
import { stripHtml } from '../../../../utils/stripHtml'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../assets/production-flow/trash.svg'
import search from '../../../../assets/production-flow/search.svg'
import { X, Copy, ClipboardPaste } from 'lucide-react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import SortableServiceCard, {
  ServiceStageDragOverlay,
} from './components/SortableServiceCard'
import {
  resolveServiceDragLabel,
  serviceStageCollisionDetection,
} from './serviceDragUtils'
import {
  apiRootsToFormTree,
  formTreeToStagesPayload,
  findInvalidServiceStage,
  findStageInApiTree,
  collectPendingFilesByPosition,
  addChildToParent,
  reorderChildServices,
  reorderParentServices,
  canAddChild,
  updateServiceByRowKey,
  removeServiceByRowKey,
  serviceTreeToClipboard,
  clipboardToServiceTree,
  isServicesClipboardPayload,
  FormServiceItem,
} from '../stageTreeUtils'
import { MAX_CHILDREN_PER_PARENT } from '../stageNumbering'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  MainContainer,
  StyledCard,
  StyledCardHeader,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  AddButton,
  MaterialItemContainer,
  MaterialFields,
  InlineDeleteButton,
  DropdownList,
  DropdownItem,
  SectionButtons,
  HeaderActions,
  HeaderIconButton,
} from './style'
import { Textarea } from '../../../../components/styles/modal-style'
import TimeField from '../../../../components/TimeField'

interface StockInProduction {
    stockId: number | null
    resourceId: number | null
    usedUnitOfMeasurement: string
    usedQuantity: number
    id: number
    Stock: {
      id: number
      usedUnitOfMeasurement: string
      usedQuantity: number
      Code: {
        id: number
        code: string
      }
      description: string
    }
  }

interface MaterialItem {
  id: number
  materialId: number
  material: string
  unitOfMeasure: 'KG' | 'MM'
  quantityToUse: string
  isAbated?: boolean
  hasInsufficientStock?: boolean
  fromProduct?: boolean
  originalProductMaterialName?: string
  skipConflictValidation?: boolean
  stockBalance?: string
}

interface ResourceItem {
  id: number
  resourceId: number
  resource: string
  unit: string
  quantity: string
  isAbated?: boolean
  fromProduct?: boolean
  skipConflictValidation?: boolean
}

const createServiceRowKey = (serviceStageId?: number) =>
  serviceStageId != null
    ? `stage-${serviceStageId}`
    : `row-${Date.now()}-${Math.random()}`

type ServiceItem = FormServiceItem

interface Order {
  id: number
  client: string
  items: OrderItem[]
  saleNumberPloomes?: string
}

interface OrderItem {
  weight: any
  id: number
  quantity?: number
  material?: { name: string }
  product?: { name: string }
  stage?: { name: string }
  resource?: { name: string }
  stock?: { description: string }
  ploomesProductName?: string
}

interface Product {
  id: number
  name: string
  scripts?: Script[]
}

interface Script {
  id: number
  name: string
  items?: ScriptItem[]
}

interface ScriptItem {
  id: number
  type: 'rawMaterial' | 'service' | 'resource' | 'productFinal'
  order: number
  rawMaterial?: { id: number; name: string }
  rawMaterialId?: number
  rawMaterialName?: string
  service?: { id: number; name: string }
  serviceId?: number
  serviceName?: string
  resource?: { id: number; name: string }
  resourceId?: number
  resourceName?: string
  productFinal?: { id: number; name: string }
  productFinalId?: number
  productFinalName?: string
  productScript?: string | { id: number; name: string }
  weight?: number
  quantity?: number
  executionTime?: number | null
  setupTime?: number | null
  description?: string
  requiredQualification?: string
  deliveryTime?: number
  observation?: string
  company?: { id: number; name: string }
  companyId?: number
  companyName?: string
  unitOfMeasurementId?: number
  unitOfMeasurement?: { id: number; name: string }
  idealMeasurements?: string
}

interface FormState {
  codeOfOP: string
  orderNumber: string
  orderItemId: number
  item: string
  order: string
  quantity: string
  weight: string
  client: string
  materials: MaterialItem[]
  resources: ResourceItem[]
  services: ServiceItem[]
}

interface SavedStageData {
  id: number
  serviceId: number
  stageId: number
  completed: boolean
  index: number
  description?: string
  observation?: string
  executionTime?: number | null
  setupTime?: number | null
  Stage: {
    name: string
    id: number
  }
  FileInServiceStage?: {
    GeneralFile: {
      id: number
      title: string
      fileUrl: string
    }
  }[]
}

interface SubOpPreviewNode {
  tempId: string
  parentTempId: string | null
  productId: number
  productName: string
  scriptId: number
  calculatedQuantity: number
  level: number
  hierarchyLabel: string
  codeOfOP: string
}

interface SubOpScriptDetails {
  scriptName: string
  services: Array<{ id: number; name: string; description?: string }>
}

interface SubOpPreviewNode {
  tempId: string
  parentTempId: string | null
  productId: number
  productName: string
  scriptId: number
  calculatedQuantity: number
  level: number
  hierarchyLabel: string
  codeOfOP: string
}

interface SubOpScriptDetails {
  scriptName: string
  services: Array<{ id: number; name: string; description?: string }>
}

const INITIAL_FORM_STATE: FormState = {
  codeOfOP: '',
  orderNumber: '',
  orderItemId: 0,
  item: '',
  order: '',
  quantity: '',
  weight: '',
  client: '',
  materials: [],
  resources: [],
  services: [],
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const getToken = () => localStorage.getItem('token')

const CreateOP = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const returnSearch = (location.state as any)?.returnSearch
  const { id: opId } = useParams<{ id: string }>()
  const isEditMode = Boolean(opId)
  const { setFeedback } = useContext(FeedbackContext)

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE)
  const [searchMaterial, setSearchMaterial] = useState('')
  const [fixedMaterialFilters, setFixedMaterialFilters] = useState<Record<number, string>>({})
  const [searchResource, setSearchResource] = useState('')
  const [searchService, setSearchService] = useState('')
  const [searchOrder, setSearchOrder] = useState('')
  const [searchProduct, setSearchProduct] = useState('')
  const [orderOptions, setOrderOptions] = useState<Order[]>([])
  const [productOptions, setProductOptions] = useState<Product[]>([])
  const [scriptOptions, setScriptOptions] = useState<Script[]>([])
  const [materialOptions, setMaterialOptions] = useState<{ id: number; name: string; code?: string; description?: string }[]>([])
  const [resourceOptions, setResourceOptions] = useState<{ id: number; name: string }[]>([])
  const [serviceOptions, setServiceOptions] = useState<
    {
      id: number
      name: string
      description?: string
      observation?: string
      executionTime?: number | null
      setupTime?: number | null
    }[]
  >([])
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null)
  const [editingResourceIndex, setEditingResourceIndex] = useState<number | null>(null)
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null)
  const [editingServiceChildIndex, setEditingServiceChildIndex] = useState<number | null>(null)
  const [expandedServiceRowKeys, setExpandedServiceRowKeys] = useState<Set<string>>(new Set())
  const [activeServiceDragId, setActiveServiceDragId] = useState<string | null>(null)
  const serviceSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [isEditingOrder, setIsEditingOrder] = useState(false)
  const [isEditingProduct, setIsEditingProduct] = useState(false)
  const [isEditingScript, setIsEditingScript] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<{ id: number; name: string } | null>(null)
  const [selectedScript, setSelectedScript] = useState<{ id: number; name: string } | null>(null)
  const [subOpPreview, setSubOpPreview] = useState<SubOpPreviewNode[]>([])
  const [subOpScriptDetails, setSubOpScriptDetails] = useState<Record<number, SubOpScriptDetails>>({})
  const [showSubOpModal, setShowSubOpModal] = useState(false)

  const hasMaterials = form.materials.length > 0
  const hasAbatedMaterials = form.materials.some((m) => m.isAbated)
  const hasPendingMaterials = form.materials.some((m) => !m.isAbated && !m.fromProduct && !m.skipConflictValidation)
  const hasResources = form.resources.length > 0
  const hasAbatedResources = form.resources.some((r) => r.isAbated)
  const hasPendingResources = form.resources.some((r) => !r.isAbated && !r.fromProduct && !r.skipConflictValidation)

  const hasAbatedItems = hasAbatedMaterials || hasAbatedResources

  const canAddMaterial = !hasAbatedMaterials && !hasPendingResources
  const canAddResource = !hasAbatedResources && !hasPendingMaterials

  const materialMessage = hasAbatedMaterials
    ? 'Já abatidos'
    : hasPendingResources
      ? 'Remova insumos pendentes'
      : ''
  const resourceMessage = hasAbatedResources
    ? 'Já abatidos'
    : hasPendingMaterials
      ? 'Remova materiais pendentes'
      : ''

  const createEmptyMaterial = (): MaterialItem => ({
    id: Date.now(),
    materialId: 0,
    material: '',
    unitOfMeasure: 'MM',
    quantityToUse: '',
    stockBalance: '',
    hasInsufficientStock: false,
  })

  const createEmptyResource = (): ResourceItem => ({
    id: Date.now(),
    resourceId: 0,
    resource: '',
    unit: 'KG',
    quantity: '',
  })

  const createEmptyService = (): ServiceItem => ({
    rowKey: createServiceRowKey(),
    id: Date.now() + Math.random(),
    serviceId: 0,
    service: '',
    description: '',
    observation: '',
    executionTime: null,
    setupTime: null,
    existingFiles: [],
    pendingFiles: [],
    children: [],
  })

  const getItemDescription = (orderItem: OrderItem): string => {
    return stripHtml(orderItem.ploomesProductName) || 'Item sem descrição'
  }

  const getClientName = (client: any): string => {
    if (!client) return ''
    if (typeof client === 'object')
      return client.companyName || client.name || ''
    if (typeof client === 'string') return client
    return ''
  }

  const filterOrders = useCallback((orders: Order[], search: string) => {
    if (!search) return orders

    const searchLower = String(search).toLowerCase().trim()

    return orders.filter((order: any) => {
      const saleNumber = String(order.saleNumberPloomes || '').toLowerCase()
      return saleNumber.includes(searchLower)
    })
  }, [])

  const processMaterials = useCallback((data: { StockInProduction: StockInProduction[] }): MaterialItem[] => {
    if (!data.StockInProduction?.length) return []

    return data.StockInProduction
      .filter((stockItem: StockInProduction) => stockItem.stockId !== null)
      .map((stockItem: StockInProduction) => {
        const stock = stockItem.Stock || {}
        return {
          id: stockItem.id || Date.now() + Math.random(),
          materialId: stock.id || stockItem.stockId || 0,
          material: stock.Code?.code || stock.id?.toString() || `Material ID: ${stock.id}`,
          materialDescription: stock.description || '',
          unitOfMeasure: stockItem.usedUnitOfMeasurement as 'KG' | 'MM',
          quantityToUse: stockItem.usedQuantity?.toString() || '',

          
          isAbated: true,
          hasInsufficientStock: false,
        }
      })
  }, [])

  const processServices = useCallback((serviceStages: any[]): ServiceItem[] => {
    return apiRootsToFormTree(serviceStages || [])
  }, [])

  const processResources = useCallback((data: any): ResourceItem[] => {
    if (data.stockInProduction?.length > 0) {
      return data.stockInProduction
        .filter((stockItem: any) => stockItem.resourceId !== null)
        .map((stockItem: any) => {
          const resource = stockItem.Resource || {}
          return {
            id: stockItem.id || Date.now() + Math.random(),
            resourceId: resource.id || stockItem.resourceId || 0,
            resource: resource.name || resource.description || `Insumo ID: ${resource.id}`,
            unit: stockItem.usedUnitOfMeasurement || 'KG',
            quantity: stockItem.usedQuantity?.toString() || '',
            isAbated: true,
          }
        })
    }

    if (data.orderItem?.resource) {
      const resource = data.orderItem.resource
      return [
        {
          id: resource.id || Date.now() + Math.random(),
          resourceId: resource.id,
          resource: resource.name || resource.description || '',
          unit: resource.stockUnit || 'KG',
          quantity: '',
          isAbated: false,
        },
      ]
    }

    return []
  }, [])

  const validateStockFormat = (stock: any, unitOfMeasure: string): { isValid: boolean; message?: string } => {
    const formatType = stock.format?.type?.toLowerCase() || 'indefinido'
    const dim1 = stock.dimension1 || 0
    const dim2 = stock.dimension2 || 0
    const lengthInMm = stock.lengthInMm || 0
    const totalWeight = stock.totalWeight || 0
    const density = stock.material?.density || 0

    const missingFields: string[] = []

    if (unitOfMeasure === 'KG') {
      if (totalWeight <= 0) {
        missingFields.push('peso')
      }
    } else if (unitOfMeasure === 'MM') {
      if (lengthInMm <= 0) {
        missingFields.push('comprimento')
      }
    }

    if (density <= 0) {
      missingFields.push('densidade')
    }

    switch (formatType) {
      case 'redondo':
      case 'sextavado':
        if (dim1 <= 0) {
          missingFields.push('dimensão 1')
        }
        break

      case 'quadrado':
      case 'chapa':
      case 'tubo':
      case 'anel':
        if (dim1 <= 0) {
          missingFields.push('dimensão 1')
        }
        if (dim2 <= 0) {
          missingFields.push('dimensão 2')
        }
        break

      case 'indefinido':
        break

      default:
        return {
          isValid: false,
          message: `Formato de material desconhecido: ${formatType}`
        }
    }

    if (missingFields.length > 0) {
      const fieldsList = missingFields.join(', ')
      return {
        isValid: false,
        message: `Material do tipo ${formatType} sem os seguintes campos: ${fieldsList}`
      }
    }

    return { isValid: true }
  }

  const checkMaterialAvailability = async (
    materialId: number,
    quantityToUse: number,
    unitOfMeasure: string,
  ): Promise<{ available: boolean; message?: string }> => {
    try {
      const response = await ApiService.productionFlow.getRawMaterialById(materialId)

      const stock = response.data
      if (!stock) {
        return {
          available: false,
          message: 'Material não encontrado no estoque',
        }
      }

      const formatValidation = validateStockFormat(stock, unitOfMeasure)
      if (!formatValidation.isValid) {
        return {
          available: false,
          message: formatValidation.message,
        }
      }

      const availableLengthInMm = stock.lengthInMm || 0
      const availableWeight = stock.totalWeight || 0

      if (unitOfMeasure === 'KG') {
        if (quantityToUse > availableWeight) {
          return {
            available: false,
            message: `Quantidade insuficiente. Disponível: ${availableWeight.toFixed(2)} KG`,
          }
        }
      } else if (unitOfMeasure === 'MM') {
        if (quantityToUse > availableLengthInMm) {
          return {
            available: false,
            message: `Comprimento insuficiente. Disponível: ${availableLengthInMm.toFixed(2)} MM`,
          }
        }
      }

      return { available: true }
    } catch (error) {
      return { available: true }
    }
  }

  const loadScriptItems = useCallback(async (scriptId: number) => {
    try {
      const response = await ApiService.productFinal.getScript(scriptId)

      const script = response.data
      if (!script || !script.items || script.items.length === 0) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Script não possui itens',
        })
        return
      }

      const newMaterials: MaterialItem[] = []
      const newResources: ResourceItem[] = []
      const newServices: ServiceItem[] = []

      const loadProductFinalItems = async (
        productFinalId: number,
        productScriptRef?: string | { id: number; name: string },
      ) => {
        try {
          const productResponse = await ApiService.productFinal.findById(productFinalId)

          const product = productResponse.data

          if (product.scripts && product.scripts.length > 0) {
            const selectedScriptId =
              typeof productScriptRef === 'object'
                ? productScriptRef?.id
                : productScriptRef
                  ? parseInt(productScriptRef, 10)
                  : null
            const productScript = selectedScriptId
              ? product.scripts.find((s: any) => s.id === selectedScriptId) || product.scripts[0]
              : product.scripts[0]

            const scriptResponse = await ApiService.productFinal.getScript(productScript.id)

            return scriptResponse.data.items || []
          }

          return []
        } catch (error) {
          console.error(`Erro ao carregar itens do produto ${productFinalId}:`, error)
          return []
        }
      }

      for (const item of script.items) {
        if (item.type === 'rawMaterial' && item.rawMaterial) {
          const materialName = item.rawMaterial.name || item.rawMaterialName || ''
          newMaterials.push({
            id: Date.now() + Math.random(),
            materialId: item.rawMaterial.id || item.rawMaterialId || 0,
            material: materialName,
            unitOfMeasure: 'KG',
            quantityToUse: item.weight?.toString() || '',
            isAbated: false,
            hasInsufficientStock: false,
            fromProduct: true,
            originalProductMaterialName: materialName,
            skipConflictValidation: true,
          })
        } else if (item.type === 'resource' && item.resource) {
          const unitName = item.unitOfMeasurement?.name || 'KG'
          newResources.push({
            id: Date.now() + Math.random(),
            resourceId: item.resource.id || item.resourceId || 0,
            resource: item.resource.name || item.resourceName || '',
            unit: unitName,
            quantity: item.weight?.toString() || item.quantity?.toString() || '',
            isAbated: false,
            fromProduct: true,
            skipConflictValidation: true,
          })
        } else if (item.type === 'service' && item.service) {
          newServices.push({
            rowKey: createServiceRowKey(),
            id: Date.now() + Math.random(),
            serviceId: item.service.id || item.serviceId || 0,
            service: item.service.name || item.serviceName || '',
            description: item.description || '',
            observation: item.observation || '',
            executionTime: item.executionTime ?? null,
            setupTime: item.setupTime ?? null,
            fromProduct: true,
          })
        } else if (item.type === 'productFinal' && (item.productFinal || item.productFinalId)) {
          const productFinalId = item.productFinal?.id || item.productFinalId
          if (productFinalId) {
            const productItems = await loadProductFinalItems(productFinalId, item.productScript)

            for (const subItem of productItems) {
              if (subItem.type === 'rawMaterial' && subItem.rawMaterial) {
                const materialName = subItem.rawMaterial.name || subItem.rawMaterialName || ''
                newMaterials.push({
                  id: Date.now() + Math.random(),
                  materialId: subItem.rawMaterial.id || subItem.rawMaterialId || 0,
                  material: materialName,
                  unitOfMeasure: 'KG',
                  quantityToUse: subItem.weight?.toString() || '',
                  isAbated: false,
                  hasInsufficientStock: false,
                  fromProduct: true,
                  originalProductMaterialName: materialName,
                  skipConflictValidation: true,
                })
              } else if (subItem.type === 'resource' && subItem.resource) {
                const unitName = subItem.unitOfMeasurement?.name || 'KG'
                newResources.push({
                  id: Date.now() + Math.random(),
                  resourceId: subItem.resource.id || subItem.resourceId || 0,
                  resource: subItem.resource.name || subItem.resourceName || '',
                  unit: unitName,
                  quantity: subItem.weight?.toString() || subItem.quantity?.toString() || '',
                  isAbated: false,
                  fromProduct: true,
                  skipConflictValidation: true,
                })
              } else if (subItem.type === 'service' && subItem.service) {
                newServices.push({
                  rowKey: createServiceRowKey(),
                  id: Date.now() + Math.random(),
                  serviceId: subItem.service.id || subItem.serviceId || 0,
                  service: subItem.service.name || subItem.serviceName || '',
                  description: subItem.description || '',
                  observation: subItem.observation || '',
                  executionTime: subItem.executionTime ?? null,
                  setupTime: subItem.setupTime ?? null,
                  fromProduct: true,
                })
              }
            }
          }
        }
      }

      setForm(prev => ({
        ...prev,
        materials: [...prev.materials, ...newMaterials],
        resources: [...prev.resources, ...newResources],
        services: [...prev.services, ...newServices],
      }))

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `${newMaterials.length} material(is), ${newResources.length} insumo(s) e ${newServices.length} serviço(s) carregados do script.`,
      })
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao carregar itens do script.',
      })
    }
  }, [setFeedback])

  const loadSubOpPreview = useCallback(async (scriptId: number) => {
    if (!isEditMode || !opId) {
      setSubOpPreview([])
      return
    }

    try {
      const response = await ApiService.productionFlow.previewSubOps({
        serviceId: parseInt(opId, 10),
        scriptId,
      })
      const nodes = (response.data || []) as SubOpPreviewNode[]
      setSubOpPreview(nodes)
      setSubOpScriptDetails({})
      if (nodes.length > 0) {
        setShowSubOpModal(true)
      }
    } catch (error) {
      setSubOpPreview([])
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao gerar preview da árvore de OPs.',
      })
    }
  }, [isEditMode, opId, setFeedback])

  useEffect(() => {
    const loadScriptDetails = async () => {
      if (!showSubOpModal || subOpPreview.length === 0) return
      try {
        const uniqueScriptIds = [...new Set(subOpPreview.map((node) => node.scriptId))]
        const responses = await Promise.all(
          uniqueScriptIds.map(async (scriptId) => {
            const response = await ApiService.productFinal.getScript(scriptId)
            const script = response.data
            const services =
              (script?.items || [])
                .filter((item: any) => item.type === 'service')
                .map((item: any) => ({
                  id: item.service?.id || item.serviceId || item.id,
                  name: item.service?.name || item.serviceName || 'Serviço',
                  description: item.description || item.observation || '',
                })) || []

            return [
              scriptId,
              {
                scriptName: script?.name || `Script ${scriptId}`,
                services,
              } satisfies SubOpScriptDetails,
            ] as const
          }),
        )

        setSubOpScriptDetails(Object.fromEntries(responses))
      } catch (error) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro ao carregar detalhes dos scripts das OPs detectadas.',
        })
      }
    }

    loadScriptDetails()
  }, [showSubOpModal, subOpPreview, setFeedback])

  const fetchOPData = useCallback(async () => {
    if (!isEditMode || !opId) return

    setIsLoading(true)
    try {
      const [basicResponse, serviceStagesResponse] = await Promise.all([
        ApiService.productionFlow.getService(parseInt(opId)),
        ApiService.productionFlow.getServicesOfOp(opId),
      ])

      const processedMaterials = processMaterials(basicResponse.data)
      const processedResources = processResources(basicResponse.data)

      const serviceStages = serviceStagesResponse.data || []
      const processedServices = processServices(serviceStages)

      let orderItem = basicResponse.data.OrderItem
      let order = orderItem?.Order

      if (!orderItem && basicResponse.data.orderItemId) {
        try {
          const orderItemResponse = await ApiService.steelOrders.findOrderItemById(basicResponse.data.orderItemId)
          orderItem = orderItemResponse.data
          order = orderItem?.Order
        } catch (error) {
          //
        }
      }

      setForm({
        codeOfOP: basicResponse.data.codeOfOP,
        orderNumber: `OP-${opId}`,
        orderItemId: basicResponse.data.orderItemId || 0,
        item: orderItem ? getItemDescription(orderItem) : 'Item do pedido',
        order: (order as any)?.saleNumberPloomes || order?.id?.toString() || '',
        quantity:
          basicResponse.data.calculatedQuantity?.toString() ||
          orderItem?.quantity?.toString() ||
          orderItem?.quantityToProduce?.toString() ||
          '',
        weight: orderItem?.weight?.toString() || '',
        client: getClientName(
          order?.Company_Order_clientIdToCompany || order?.Client || basicResponse.data.client,
        ),
        materials: processedMaterials,
        resources: processedResources,
        services: processedServices,
      })

      const svc = basicResponse.data as Record<string, any>
      let nextProduct: { id: number; name: string } | null = null
      let nextScript: { id: number; name: string } | null = null
      let scriptsList: Script[] = []

      const scriptIdNum =
        svc.scriptId != null && svc.scriptId !== ''
          ? Number(svc.scriptId)
          : svc.Script?.id != null
            ? Number(svc.Script.id)
            : null

      if (scriptIdNum != null && !Number.isNaN(scriptIdNum)) {
        const incScript = svc.Script
        const incName = incScript?.name
        const incProd = incScript?.Product ?? incScript?.product
        if (incName) nextScript = { id: scriptIdNum, name: String(incName) }
        if (incProd?.id) {
          nextProduct = {
            id: incProd.id,
            name: String(incProd.name || `Produto ${incProd.id}`),
          }
        }
        if (!nextScript || !nextProduct) {
          try {
            const scriptRes = await ApiService.productFinal.getScript(scriptIdNum)
            const sc = scriptRes.data
            if (!nextScript && sc?.name) nextScript = { id: scriptIdNum, name: String(sc.name) }
            if (!nextProduct && sc?.product?.id) {
              nextProduct = {
                id: sc.product.id,
                name: String(sc.product.name || `Produto ${sc.product.id}`),
              }
            }
          } catch {
            if (!nextScript && incName) nextScript = { id: scriptIdNum, name: String(incName) }
          }
        }
      }

      const pidRaw =
        svc.productId != null && svc.productId !== ''
          ? Number(svc.productId)
          : svc.Product?.id ?? svc.product?.id
      if (!nextProduct && pidRaw != null && !Number.isNaN(Number(pidRaw))) {
        const pid = Number(pidRaw)
        const p = svc.Product || svc.product
        nextProduct = {
          id: pid,
          name: String(p?.name || `Produto ${pid}`),
        }
      }

      if (!nextScript && scriptIdNum != null && !Number.isNaN(scriptIdNum)) {
        nextScript = {
          id: scriptIdNum,
          name: String(svc.Script?.name || `Roteiro #${scriptIdNum}`),
        }
      }

      if (nextProduct?.id) {
        try {
          const productResponse = await ApiService.productFinal.findById(nextProduct.id)
          const prod = productResponse.data
          const rawScripts = prod?.scripts
          if (Array.isArray(rawScripts)) scriptsList = rawScripts as Script[]
        } catch {
          //
        }
      }

      setSelectedProduct(nextProduct)
      setSelectedScript(nextScript)
      setScriptOptions(scriptsList)
      setSubOpPreview([])
      setSubOpScriptDetails({})
      setShowSubOpModal(false)
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao carregar dados da OP',
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    isEditMode,
    opId,
    processMaterials,
    processResources,
    processServices,
    setFeedback,
  ])

  useEffect(() => {
    fetchOPData()
  }, [fetchOPData])

  const fetchData = useCallback(
    async (url: string, setter: any, search?: string) => {
      try {
        const response = await ApiService.get(url, search ? { search } : {})

        if (response.data) {
          if (Array.isArray(response.data)) {
            setter(response.data)
          } else if (response.data.data && Array.isArray(response.data.data)) {
            setter(response.data.data)
          } else {
            setter([])
          }
        } else {
          setter([])
        }
      } catch (error) {
        setter([])
      }
    },
    [],
  )

  const fetchOrders = useCallback(async (search?: string) => {
    try {
      const response = await ApiService.steelOrders.findOrders(search ? { search } : undefined)

      let orders = []
      if (Array.isArray(response.data)) {
        orders = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        orders = response.data.data
      }

      setOrderOptions(orders)
    } catch (error) {
      setOrderOptions([])
    }
  }, [])

  useEffect(() => {
    if (isEditingOrder) {
      const timeoutId = setTimeout(() => {
        fetchOrders(searchOrder)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingOrder, searchOrder, fetchOrders])

  const fetchProducts = useCallback(async (search?: string) => {
    try {
      const response = await ApiService.productFinal.findAll({ limit: 100, ...(search ? { search } : {}) })

      let products = []
      if (Array.isArray(response.data)) {
        products = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        products = response.data.data
      }

      setProductOptions(products)
    } catch (error) {
      setProductOptions([])
    }
  }, [])

  useEffect(() => {
    if (isEditingProduct) {
      const timeoutId = setTimeout(() => {
        fetchProducts(searchProduct)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingProduct, searchProduct, fetchProducts])

  useEffect(() => {
    if (editingMaterialIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          const filterToUse = fixedMaterialFilters[editingMaterialIndex] || searchMaterial
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
            setMaterialOptions,
            filterToUse,
          )
        },
        searchMaterial ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingMaterialIndex, searchMaterial, fixedMaterialFilters, fetchData])

  useEffect(() => {
    if (editingResourceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/resources`,
            setResourceOptions,
            searchResource,
          )
        },
        searchResource ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingResourceIndex, searchResource, fetchData])

  useEffect(() => {
    if (editingServiceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/stages`,
            setServiceOptions,
            searchService,
          )
        },
        searchService ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingServiceIndex, searchService, fetchData])

  const selectOrder = useCallback((order: Order, orderItem: OrderItem) => {
    setForm((prev) => ({
      ...prev,
      order: (order as any).saleNumberPloomes || order.id.toString(),
      orderItemId: orderItem.id,
      item: getItemDescription(orderItem),
      client: order.client,
      weight: orderItem?.weight?.toString() || '',
      quantity: orderItem?.quantity?.toString() || '',
    }))
    setIsEditingOrder(false)
    setSearchOrder('')
  }, [])

  const selectProduct = useCallback((product: Product) => {
    const hasAbatedItems = form.materials.some(m => m.isAbated) || form.resources.some(r => r.isAbated)

    if (hasAbatedItems) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Reverta a OP antes de adicionar mais itens.',
      })
      return
    }

    setSelectedProduct({ id: product.id, name: product.name })
    setSelectedScript(null)
    setSubOpPreview([])
    setScriptOptions(product.scripts || [])
    setIsEditingProduct(false)
    setSearchProduct('')
  }, [form.materials, form.resources, setFeedback])

  const selectScript = useCallback((script: Script) => {
    const hasAbatedItems = form.materials.some(m => m.isAbated) || form.resources.some(r => r.isAbated)

    if (hasAbatedItems) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Reverta a OP antes de adicionar mais itens.',
      })
      return
    }

    setForm((prev) => ({
      ...prev,
      materials: prev.materials.filter(m => m.isAbated),
      resources: prev.resources.filter(r => r.isAbated),
      services: [],
    }))
    setSelectedScript({ id: script.id, name: script.name })
    setIsEditingScript(false)
    loadScriptItems(script.id)
    loadSubOpPreview(script.id)
  }, [form.materials, form.resources, loadScriptItems, loadSubOpPreview, setFeedback])

  const addMaterial = useCallback(() => {
    if (!canAddMaterial) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: materialMessage || 'Não é possível adicionar material',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      materials: [...prev.materials, createEmptyMaterial()],
    }))
    setEditingMaterialIndex(form.materials.length)
    setSearchMaterial('')
  }, [canAddMaterial, materialMessage, form.materials.length, setFeedback])

  const addResource = useCallback(() => {
    if (!canAddResource) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: resourceMessage || 'Não é possível adicionar insumo',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      resources: [...prev.resources, createEmptyResource()],
    }))
    setEditingResourceIndex(form.resources.length)
    setSearchResource('')
  }, [canAddResource, resourceMessage, form.resources.length, setFeedback])

  const addService = useCallback(() => {
    const newService = createEmptyService()
    setForm((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }))
    setEditingServiceIndex(form.services.length)
    setEditingServiceChildIndex(null)
    setSearchService('')
  }, [form.services.length])

  const copyServicesToClipboard = useCallback(async () => {
    if (form.services.length === 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Nenhum serviço para copiar.',
      })
      return
    }

    try {
      const payload = serviceTreeToClipboard(form.services)
      await navigator.clipboard.writeText(JSON.stringify(payload))
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `${form.services.length} serviço(s) copiado(s) para a área de transferência.`,
      })
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao copiar serviços para a área de transferência.',
      })
    }
  }, [form.services, setFeedback])

  const pasteServicesFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!text) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Área de transferência vazia.',
        })
        return
      }

      let parsed: unknown
      try {
        parsed = JSON.parse(text)
      } catch {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Conteúdo inválido na área de transferência.',
        })
        return
      }

      if (!isServicesClipboardPayload(parsed)) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Nenhum serviço válido encontrado na área de transferência.',
        })
        return
      }

      const pastedServices = clipboardToServiceTree(parsed)
      setForm((prev) => ({ ...prev, services: pastedServices }))
      setEditingServiceIndex(null)
      setEditingServiceChildIndex(null)
      setExpandedServiceRowKeys(new Set())
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `${pastedServices.length} serviço(s) colado(s) da área de transferência.`,
      })
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao colar serviços da área de transferência.',
      })
    }
  }, [setFeedback])

  const addChildService = useCallback((parentIndex: number) => {
    setForm((prev) => {
      const parent = prev.services[parentIndex]
      if (!parent || !canAddChild(parent)) return prev
      const child: ServiceItem = {
        ...createEmptyService(),
        rowKey: `child-${Date.now()}-${Math.random()}`,
        id: Date.now() + Math.random(),
      }
      return {
        ...prev,
        services: addChildToParent(prev.services, parentIndex, child),
      }
    })
    setEditingServiceIndex(parentIndex)
    setEditingServiceChildIndex(
      form.services[parentIndex]?.children?.length ?? 0,
    )
    setSearchService('')
  }, [form.services])

  const updateItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      id: number | string,
      field: string,
      value: any,
    ) => {
      if (section === 'services') {
        setForm((prev) => ({
          ...prev,
          services: updateServiceByRowKey(prev.services, String(id), (item) => ({
            ...item,
            [field]: value,
          })),
        }))
        return
      }

      setForm((prev) => ({
        ...prev,
        [section]: prev[section].map((item) =>
          item.id === id ? {
            ...item,
            [field]: value,
            ...(field === 'unitOfMeasure' && { quantityToUse: '' }),
            fromProduct: item.fromProduct,
            ...(('skipConflictValidation' in item) && {
              skipConflictValidation: (item as MaterialItem | ResourceItem).skipConflictValidation
            }),
          } : item,
        ),
      }))

      if (section === 'materials' && (field === 'quantityToUse' || field === 'unitOfMeasure')) {
        const material = form.materials.find((m) => m.id === id)

        if (material && material.materialId > 0) {

          const unit = field === 'unitOfMeasure' ? value : material.unitOfMeasure
          const quantity = field === 'unitOfMeasure'
            ? 0
            : parseFloat(value.replace(',', '.'))


          const availability = await checkMaterialAvailability(material.materialId, quantity || 0, unit)

          try {
            const response = await ApiService.productionFlow.getRawMaterialById(material.materialId)

            const stock = response.data
            const unitStr = String(unit || '').trim().toUpperCase();
            const isKg = unitStr === 'KG';
            let stockVal = isKg ? stock.totalWeight : (stock.totalLength || stock.lengthInMm);

            if (!isKg && Number(stock.totalWeight) > 0 && !stockVal) {
              stockVal = stock.totalWeight;
            }

            const formattedStock = stockVal !== undefined ? `${stockVal.toFixed(2)} ${unit}` : "0.00"

            setForm((prev) => ({
              ...prev,
              materials: prev.materials.map((m) =>
                m.id === id ? {
                  ...m,
                  [field]: value,
                  hasInsufficientStock: !availability.available,
                  stockBalance: formattedStock,
                  fromProduct: m.fromProduct,
                  skipConflictValidation: m.skipConflictValidation,
                } : m
              ),
            }))
          } catch (error) {
            console.error("Erro ao atualizar placeholder:", error)
          }
          if (quantity > 0 && !isNaN(quantity) && !availability.available) {
            setFeedback({
              isOpen: true,
              statusOk: false,
              message: availability.message || 'Quantidade insuficiente no estoque',
            })
          }
        }
      }
    }, [form.materials, setFeedback, checkMaterialAvailability]
  )

  const selectItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      index: number,
      option: {
        id: number
        name: string
        description?: string
        observation?: string
        executionTime?: number | null
        setupTime?: number | null
      },
      childIndex?: number,
    ) => {
      const fieldMap = {
        materials: { idField: 'materialId', nameField: 'material' },
        resources: { idField: 'resourceId', nameField: 'resource' },
        services: { idField: 'serviceId', nameField: 'service' },
      };

      const { idField, nameField } = fieldMap[section];

      if (section === 'materials') {

        const material = form.materials[index];
        const currentUnit = material?.unitOfMeasure || 'MM';

        const weight = (option as any).totalWeight || 0;
        const length = (option as any).totalLength || (option as any).lengthInMm || 0;

        const initialBalance = currentUnit === 'KG'
          ? `${Number(weight).toFixed(2)} KG`
          : `${Number(length).toFixed(2)} MM`;



        setFixedMaterialFilters((prev) => {
          const newFilters = { ...prev };
          delete newFilters[index];
          return newFilters;
        });

        const quantity = material.quantityToUse ? parseFloat(material.quantityToUse.replace(',', '.')) : 0;

        const processSelection = async () => {
          const availability = await checkMaterialAvailability(
            option.id,
            quantity,
            currentUnit,
          );

          setForm((prev) => ({
            ...prev,
            materials: prev.materials.map((item, i) => {
              if (i !== index) return item;

              return {
                ...item,
                [idField]: option.id,
                [nameField]: (option as any).code || option.name,
                stockBalance: initialBalance,
                hasInsufficientStock: quantity > 0 ? !availability.available : false,
                fromProduct: item.fromProduct,
                skipConflictValidation: item.skipConflictValidation,
                originalProductMaterialName: item.originalProductMaterialName,
              };
            }),
          }));

          if (quantity > 0 && !availability.available) {
            setFeedback({
              isOpen: true,
              statusOk: false,
              message: availability.message || 'Quantidade insuficiente no estoque',
            });
          }
        };

        processSelection();

        setEditingMaterialIndex(null);
        setSearchMaterial('');
        return;
      }
      if (section === 'services') {
        setForm((prev) => ({
          ...prev,
          services: prev.services.map((parent, pi) => {
            if (pi !== index) return parent
            if (childIndex === undefined) {
              return {
                ...parent,
                [idField]: option.id,
                [nameField]: option.name,
                description: option.description || '',
                observation: option.observation || '',
                executionTime: option.executionTime ?? null,
                setupTime: option.setupTime ?? null,
              }
            }
            const children = [...(parent.children ?? [])]
            const child = children[childIndex]
            if (!child) return parent
            children[childIndex] = {
              ...child,
              [idField]: option.id,
              [nameField]: option.name,
              description: option.description || '',
              observation: option.observation || '',
              executionTime: option.executionTime ?? null,
              setupTime: option.setupTime ?? null,
            }
            return { ...parent, children }
          }),
        }))
      } else {
        setForm((prev) => ({
          ...prev,
          [section]: prev[section].map((item, i) =>
            i === index
              ? { ...item, [idField]: option.id, [nameField]: option.name }
              : item,
          ),
        }));
      }

      setEditingResourceIndex(null);
      setEditingServiceIndex(null);
      setEditingServiceChildIndex(null);
      setSearchResource('');
      setSearchService('');
    },
    [form.materials, setFeedback, checkMaterialAvailability],
  );

  const removeItem = useCallback(
    (section: 'materials' | 'resources' | 'services', id: number) => {
      setForm((prev) => ({
        ...prev,
        [section]: prev[section].filter((item) => item.id !== id),
      }))
    },
    [],
  )

  const addServiceFiles = useCallback((rowKey: string, files: File[]) => {
    setForm((prev) => ({
      ...prev,
      services: updateServiceByRowKey(prev.services, rowKey, (item) => ({
        ...item,
        pendingFiles: [...(item.pendingFiles || []), ...files].slice(0, 5),
      })),
    }))
  }, [])

  const removePendingServiceFile = useCallback((rowKey: string, fileIndex: number) => {
    setForm((prev) => ({
      ...prev,
      services: updateServiceByRowKey(prev.services, rowKey, (item) => ({
        ...item,
        pendingFiles: (item.pendingFiles || []).filter((_, index) => index !== fileIndex),
      })),
    }))
  }, [])

  const downloadPendingServiceFile = useCallback((file: File) => {
    const fileUrl = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = file.name
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(fileUrl)
  }, [])

  const removeExistingServiceFile = useCallback(async (serviceStageId: number, fileId: number) => {
    try {
      await ApiService.delete(`/production-flow/service/stage/${serviceStageId}/files/${fileId}`)

      setForm((prev) => ({
        ...prev,
        services: prev.services.map((parent) => {
          const filterFiles = (item: ServiceItem) =>
            item.serviceStageId === serviceStageId
              ? {
                  ...item,
                  existingFiles: (item.existingFiles || []).filter(
                    (file) => file.id !== fileId,
                  ),
                }
              : item
          if (parent.serviceStageId === serviceStageId) {
            return filterFiles(parent)
          }
          return {
            ...parent,
            children: (parent.children ?? []).map((child) => filterFiles(child)),
          }
        }),
      }))
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao remover arquivo da etapa.',
      })
    }
  }, [setFeedback])

  const downloadExistingServiceFile = useCallback(async (serviceStageId: number, fileId: number, fileName: string) => {
    try {
      const response = await ApiService.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${serviceStageId}/files/${fileId}/download`,
      )
      const link = document.createElement('a')
      link.href = response.data.url
      link.download = fileName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao baixar arquivo da etapa.',
      })
    }
  }, [setFeedback])

  const toggleServiceExpand = useCallback((rowKey: string) => {
    setExpandedServiceRowKeys((prev) => {
      const next = new Set(prev)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }, [])

  const removeService = useCallback((rowKey: string) => {
    setForm((prev) => ({
      ...prev,
      services: removeServiceByRowKey(prev.services, rowKey),
    }))
    setExpandedServiceRowKeys((prev) => {
      const next = new Set(prev)
      next.delete(rowKey)
      return next
    })
  }, [])

  const handleServiceDragStart = useCallback((event: DragStartEvent) => {
    setActiveServiceDragId(String(event.active.id))
  }, [])

  const handleServiceDragEnd = useCallback((event: DragEndEvent) => {
    setActiveServiceDragId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeStr = String(active.id)
    const overStr = String(over.id)

    if (activeStr.startsWith('root:') && overStr.startsWith('root:')) {
      const activeKey = activeStr.slice(5)
      const overKey = overStr.slice(5)
      setForm((prev) => {
        const oldIndex = prev.services.findIndex((s) => s.rowKey === activeKey)
        const newIndex = prev.services.findIndex((s) => s.rowKey === overKey)
        if (oldIndex < 0 || newIndex < 0) return prev
        if (prev.services[oldIndex].completed || prev.services[newIndex].completed) return prev
        return { ...prev, services: arrayMove(prev.services, oldIndex, newIndex) }
      })
      return
    }

    if (activeStr.startsWith('child:') && overStr.startsWith('child:')) {
      const activeParts = activeStr.split(':')
      const overParts = overStr.split(':')
      const activeParentKey = activeParts[1]
      const overParentKey = overParts[1]
      const activeChildKey = activeParts.slice(2).join(':')
      const overChildKey = overParts.slice(2).join(':')
      if (activeParentKey !== overParentKey) return

      setForm((prev) => {
        const parentIndex = prev.services.findIndex(
          (s) => s.rowKey === activeParentKey,
        )
        if (parentIndex < 0) return prev
        const children = [...(prev.services[parentIndex].children ?? [])]
        const oldIndex = children.findIndex((c) => c.rowKey === activeChildKey)
        const newIndex = children.findIndex((c) => c.rowKey === overChildKey)
        if (oldIndex < 0 || newIndex < 0) return prev
        if (children[oldIndex].completed || children[newIndex].completed) return prev
        const reordered = arrayMove(children, oldIndex, newIndex)
        return {
          ...prev,
          services: prev.services.map((p, i) =>
            i === parentIndex ? { ...p, children: reordered } : p,
          ),
        }
      })
    }
  }, [])

  const handleServiceDragCancel = useCallback(() => {
    setActiveServiceDragId(null)
  }, [])

  const activeServiceDragLabel =
    activeServiceDragId && resolveServiceDragLabel(form.services, activeServiceDragId)

  const validateForm = useCallback(async (): Promise<string | null> => {
    if (!form.orderItemId) return 'Selecione um pedido e um item.'

    if (form.services.length > 0) {
      const invalid = findInvalidServiceStage(form.services)
      if (invalid) {
        return 'Todas as etapas devem ter um serviço selecionado antes de salvar.'
      }
    }

    const pendingMaterials = form.materials.filter(
      (m) => !m.isAbated && !m.fromProduct && !m.skipConflictValidation
    )
    const pendingResources = form.resources.filter(
      (r) => !r.isAbated && !r.fromProduct && !r.skipConflictValidation
    )

    if (pendingMaterials.length > 0 && pendingResources.length > 0) {
      return 'Não é possível ter materiais e insumos pendentes simultaneamente. Remova um dos tipos.'
    }

    if (
      form.materials.length > 0 &&
      form.materials.some(
        (mat) =>
          !mat.materialId ||
          mat.materialId === 0 ||
          !mat.quantityToUse ||
          parseFloat(mat.quantityToUse.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar materiais, todos devem ter um material selecionado e quantidade válida.'
    }

    if (pendingMaterials.length > 0) {
      const allSelected = pendingMaterials.every((m) => m.materialId > 0)
      if (!allSelected) {
        return 'Todos os materiais devem ter um material selecionado antes de salvar.'
      }

      for (const material of pendingMaterials) {
        const quantity = parseFloat(material.quantityToUse.replace(',', '.'))
        if (quantity > 0 && !isNaN(quantity)) {
          const availability = await checkMaterialAvailability(
            material.materialId,
            quantity,
            material.unitOfMeasure,
          )
          if (!availability.available) {
            return availability.message || 'Quantidade insuficiente em um ou mais materiais.'
          }
        }
      }
    }

    if (
      form.resources.length > 0 &&
      form.resources.some(
        (res) =>
          !res.resourceId ||
          res.resourceId === 0 ||
          !res.quantity ||
          parseFloat(res.quantity.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar insumos, todos devem ter um insumo selecionado e quantidade válida.'
    }

    if (pendingResources.length > 0) {
      const allSelected = pendingResources.every((r) => r.resourceId > 0)
      if (!allSelected) {
        return 'Todos os insumos devem ter um insumo selecionado antes de salvar.'
      }
    }

    const resourcesWithInvalidUnit = form.resources.filter(
      (res) => res.unit === 'UN' && !Number.isInteger(parseFloat(res.quantity.replace(',', '.')))
    )
    if (resourcesWithInvalidUnit.length > 0) {
      return 'Insumos com unidade "UN" devem ter quantidade em números inteiros.'
    }

    return null
  }, [form])

  const discountItemsAutomatically = async (
    opId: number,
    materials: MaterialItem[],
    resources: ResourceItem[],
  ) => {
    try {
      const itemsToDiscount: any = {}

      if (materials.length > 0) {
        itemsToDiscount.materials = materials.map((item, index) => ({
          id: index + 1,
          material: {
            id: item.materialId,
            name: item.material,
          },
          quantityToUse: parseFloat(item.quantityToUse.replace(',', '.')),
          unitOfMeasure: item.unitOfMeasure,
        }))
      }

      if (resources.length > 0) {
        itemsToDiscount.resources = resources.map((item, index) => ({
          id: index + 1,
          material: {
            id: item.resourceId,
            name: item.resource,
          },
          quantityToUse: parseFloat(item.quantity.replace(',', '.')),
          unitOfMeasure: item.unit,
        }))
      }

      await ApiService.pcp.discountItems(opId, itemsToDiscount)

      return { success: true }
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido no abatimento'
      return { success: false, error: errorMessage }
    }
  }

    const collectAbatimentoMessage = async (createdOpId: number): Promise<string> => {
    if (!createdOpId) return ''

    let abatimentoMessage = ''

    const materialsToAbate = form.materials.filter((m) => !m.isAbated)
    const resourcesToAbate = form.resources.filter((r) => !r.isAbated)

    const productMaterials = materialsToAbate.filter((m) => m.skipConflictValidation)
    const productResources = resourcesToAbate.filter((r) => r.skipConflictValidation)

    const manualMaterials = materialsToAbate.filter((m) => !m.skipConflictValidation)
    const manualResources = resourcesToAbate.filter((r) => !r.skipConflictValidation)

    const abatimentoResults: string[] = []

    if (productMaterials.length > 0) {
      const materialProductResult = await discountItemsAutomatically(
        createdOpId,
        productMaterials,
        [],
      )

      if (materialProductResult.success) {
        abatimentoResults.push(`${productMaterials.length} material(is) do produto abatidos`)
      } else {
        abatimentoResults.push(
          `Falha ao abater materiais do produto: ${materialProductResult.error}`,
        )
      }
    }

    if (productResources.length > 0) {
      const resourceProductResult = await discountItemsAutomatically(
        createdOpId,
        [],
        productResources,
      )

      if (resourceProductResult.success) {
        abatimentoResults.push(`${productResources.length} insumo(s) do produto abatidos`)
      } else {
        abatimentoResults.push(
          `Falha ao abater insumos do produto: ${resourceProductResult.error}`,
        )
      }
    }

    if (manualMaterials.length > 0 || manualResources.length > 0) {
      const manualResult = await discountItemsAutomatically(
        createdOpId,
        manualMaterials,
        manualResources,
      )

      if (manualResult.success) {
        const messages: string[] = []
        if (manualMaterials.length > 0) {
          messages.push(`${manualMaterials.length} material(is) manual(is)`)
        }
        if (manualResources.length > 0) {
          messages.push(`${manualResources.length} insumo(s) manual(is)`)
        }
        abatimentoResults.push(`${messages.join(' e ')} abatidos`)
      } else {
        abatimentoResults.push(`Falha ao abater itens manuais: ${manualResult.error}`)
      }
    }

    if (abatimentoResults.length > 0) {
      abatimentoMessage = ` ${abatimentoResults.join('. ')}.`
    }

    return abatimentoMessage
  }
  const handleConfirmSubOpModal = async () => {
    if (!isEditMode || !opId) return

    if (!getToken()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Faça login novamente.',
      })
      return
    }

    if (subOpPreview.length === 0) {
      setShowSubOpModal(false)
      return
    }

    if (!selectedScript?.id) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Selecione um script antes de gerar as OPs filhas.',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const serviceId = parseInt(opId, 10)
      await ApiService.productionFlow.generateSubOps(serviceId, {
        scriptId: selectedScript.id,
        productId: selectedProduct?.id,
      })

      setSubOpPreview([])
      setSubOpScriptDetails({})
      setShowSubOpModal(false)
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'OPs filhas geradas com sucesso.',
      })
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Erro desconhecido'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: `Erro ao gerar OPs filhas: ${errorMessage}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelSubOpModal = useCallback(() => {
    setShowSubOpModal(false)
  }, [])

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    const validationError = await validateForm()
    if (validationError) {
      setFeedback({ isOpen: true, statusOk: false, message: validationError })
      return
    }

    if (!getToken()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Faça login novamente.',
      })
      return
    }

    setIsSubmitting(true)

    try {
      let productIdToSave = selectedProduct?.id ?? null
      if (selectedScript?.id) {
        try {
          const scriptRes = await ApiService.productFinal.getScript(selectedScript.id)
          const p = scriptRes.data?.product
          if (p?.id) productIdToSave = p.id
        } catch {
          //
        }
      }

      const dataToSend: any = {
        orderItemId: parseInt(form.orderItemId.toString(), 10),
        quantity: form.quantity ? parseInt(form.quantity, 10) : null,
        scriptId: selectedScript?.id ?? null,
        productId: productIdToSave,
        stages: formTreeToStagesPayload(form.services),
      }

      let response, createdOpId
      const pendingServiceFilesByPosition = collectPendingFilesByPosition(form.services)

      if (isEditMode && opId) {
        response = await ApiService.services.updateService(parseInt(opId), dataToSend)
        createdOpId = parseInt(opId)
      } else {
        response = await ApiService.productionFlow.createService(dataToSend)
        createdOpId =
          response.data?.id ||
          (typeof response.data === 'string'
            ? parseInt(response.data.match(/OP[\s](\d+)/)?.[1] || '0')
            : 0)
      }

      if (createdOpId) {
        const hasPendingFiles = pendingServiceFilesByPosition.some((row) => row.files.length > 0)
        if (hasPendingFiles) {
          const stagesResponse = await ApiService.productionFlow.getServicesOfOp(String(createdOpId))
          const stages = Array.isArray(stagesResponse.data) ? stagesResponse.data : []
          for (const row of pendingServiceFilesByPosition) {
            const files = row.files
            if (!files.length) continue
            const stage = findStageInApiTree(stages, row.parentIndex, row.childIndex)
            if (!stage?.id) continue
            const formData = new FormData()
            files.forEach((file) => formData.append('files', file))
            await ApiService.post(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${stage.id}/upload-files`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } },
            )
          }
        }
      }

      const abatimentoMessage = await collectAbatimentoMessage(createdOpId)

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `OP ${isEditMode ? 'atualizada' : 'criada'} com sucesso!${abatimentoMessage}`,
      })

      if (returnTo) {
        navigate(returnTo, { replace: true })
      } else {
        navigate('../', { state: { returnSearch: returnSearch } })
      }
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} ordem de produção: ${errorMessage}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = useCallback(() => {
    if (returnTo) {
      navigate(returnTo, { replace: true })
    } else {
      navigate('/panel/production', { state: { returnSearch: returnSearch } })
    }
  }, [navigate, returnSearch, returnTo])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.dropdown-container')) {
        setEditingMaterialIndex(null)
        setEditingResourceIndex(null)
        setEditingServiceIndex(null)
        setEditingServiceChildIndex(null)
        setIsEditingOrder(false)
        setIsEditingProduct(false)
        setIsEditingScript(false)
        setFixedMaterialFilters({})
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const breadcrumbItems = [
    { label: 'Ordem de produção', path: '/panel/production' },
    {
      label: isEditMode
        ? 'Editar ordem de produção'
        : 'Criar ordem de produção',
    },
  ]

  if (isLoading) {
    return (
      <MainContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <p>Carregando dados da OP {opId}...</p>
        </div>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isEditMode && form.codeOfOP ? (
              <>
                Ordem de produção:{' '}
                <strong style={{ fontWeight: 700 }}>{form.codeOfOP}</strong>
              </>
            ) : (
              'Criar ordem de produção'
            )}
          </SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type='button' onClick={handleCancel}>
            Cancelar
          </CancelButton>
          <SaveButton
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <img src={save} alt='Salvar' />
            {isSubmitting
              ? isEditMode
                ? 'Atualizando...'
                : 'Criando OP...'
              : isEditMode
                ? 'Atualizar OP'
                : 'Salvar OP'}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='order'>Pedido</label>
                <input
                  type='text'
                  id='order'
                  value={isEditingOrder ? searchOrder : form.order}
                  onChange={(e) => {
                    setSearchOrder(e.target.value)
                    setIsEditingOrder(true)
                  }}
                  onFocus={() => {
                    setIsEditingOrder(true)
                    setSearchOrder(form.order)
                  }}
                  disabled={isEditMode}
                />
                {isEditingOrder && (
                  <DropdownList>
                    {filterOrders(orderOptions, searchOrder).map((order: any) => (
                      <DropdownItem
                        key={order.id}
                        onClick={() =>
                          order.items?.length > 0 &&
                          selectOrder(order, order.items[0])
                        }
                      >
                        {order.saleNumberPloomes}
                      </DropdownItem>
                    ))}
                    {filterOrders(orderOptions, searchOrder).length === 0 && (
                      <DropdownItem style={{ pointerEvents: 'none', opacity: 0.6 }}>
                        Nenhum pedido encontrado
                      </DropdownItem>
                    )}
                  </DropdownList>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='client'>Cliente</label>
                <input
                  type='text'
                  id='client'
                  value={form.client}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='item'>Item</label>
                <input
                  type='text'
                  id='item'
                  value={form.item}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='weight'>Peso</label>
                <input
                  type='number'
                  id='weight'
                  value={form.weight}
                  readOnly
                  disabled
                />
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <CardTitle>Produto</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='product'>Produto</label>
                <input
                  type='text'
                  id='product'
                  value={isEditingProduct ? searchProduct : (selectedProduct?.name || '')}
                  onChange={(e) => {
                    const value = e.target.value
                    setSearchProduct(value)
                    setIsEditingProduct(true)

                    if (!value || value.trim() === '') {
                      setSelectedProduct(null)
                      setSelectedScript(null)
                      setScriptOptions([])
                    }
                  }}
                  onFocus={() => {
                    if (!hasAbatedItems) {
                      setIsEditingProduct(true)
                      setSearchProduct(selectedProduct?.name || '')
                    }
                  }}
                  placeholder={hasAbatedItems ? "Reverta a OP antes de adicionar mais itens" : "Buscar produto..."}
                  disabled={hasAbatedItems}
                />
                {isEditingProduct && !hasAbatedItems && productOptions.length > 0 && (
                  <DropdownList>
                    {productOptions.map((product) => (
                      <DropdownItem
                        key={product.id}
                        onClick={() => selectProduct(product)}
                      >
                        {product.name}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
                {isEditingProduct && !hasAbatedItems && searchProduct && productOptions.length === 0 && (
                  <DropdownList>
                    <DropdownItem style={{ pointerEvents: 'none', opacity: 0.6 }}>
                      Nenhum produto encontrado
                    </DropdownItem>
                  </DropdownList>
                )}
              </StyledFormGroup>

              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='script'>Script</label>
                <input
                  type='text'
                  id='script'
                  value={isEditingScript ? '' : (selectedScript?.name || '')}
                  onChange={(e) => {
                    if (selectedProduct && !hasAbatedItems) {
                      setIsEditingScript(true)
                    }
                  }}
                  onFocus={() => {
                    if (selectedProduct && !hasAbatedItems) {
                      setIsEditingScript(true)
                    }
                  }}
                  onClick={() => {
                    if (selectedProduct && !hasAbatedItems) {
                      setIsEditingScript(true)
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedScript && !hasAbatedItems) {
                      e.preventDefault()
                      setSelectedScript(null)
                      setSubOpPreview([])
                      setForm((prev) => ({
                        ...prev,
                        materials: prev.materials.filter(m => m.isAbated),
                        resources: prev.resources.filter(r => r.isAbated),
                        services: [],
                      }))
                    }
                  }}
                  placeholder={
                    hasAbatedItems
                      ? "Reverta a OP antes de adicionar mais itens"
                      : selectedProduct
                        ? "Selecione um script"
                        : "Selecione um produto primeiro"
                  }
                  disabled={!selectedProduct || hasAbatedItems}
                />
                {isEditingScript && selectedProduct && !hasAbatedItems && scriptOptions.length > 0 && (
                  <DropdownList>
                    {scriptOptions.map((script) => (
                      <DropdownItem
                        key={script.id}
                        onClick={() => selectScript(script)}
                      >
                        {script.name}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
                {isEditingScript && selectedProduct && !hasAbatedItems && scriptOptions.length === 0 && (
                  <DropdownList>
                    <DropdownItem style={{ pointerEvents: 'none', opacity: 0.6 }}>
                      Este produto não possui scripts
                    </DropdownItem>
                  </DropdownList>
                )}
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Materiais</CardTitle>
            </StyledCardHeader>

            {form.materials.map((material, index) => {
              const currentStockValue = parseFloat(material.stockBalance?.split(' ')[0] || '0');
              const typedQuantity = parseFloat(material.quantityToUse.replace(',', '.')) || 0;
              const futureStock = currentStockValue - typedQuantity;
              const currentUnit = material.unitOfMeasure || 'MM';

              return (
                <MaterialItemContainer key={`${material.id}-${material.materialId}`}>
                  <MaterialFields>
                    <StyledFormGroup className='dropdown-container'>
                      <label>
                        Material
                        {material.fromProduct && material.originalProductMaterialName && (
                          <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}>
                            {' '}({material.originalProductMaterialName})
                          </span>
                        )}
                      </label>
                      <input
                        type='text'
                        value={
                          editingMaterialIndex === index
                            ? searchMaterial
                            : material.material
                        }
                        onChange={(e) => {
                          setSearchMaterial(e.target.value)
                          setEditingMaterialIndex(index)
                        }}
                        onFocus={() => {
                          setEditingMaterialIndex(index)
                          if (material.fromProduct && material.originalProductMaterialName) {
                            setFixedMaterialFilters(prev => {
                              const { [index]: _, ...newFilters } = prev
                              return newFilters
                            })
                            setSearchMaterial(material.material)
                          } else {
                            setFixedMaterialFilters(prev => {
                              const newFilters = { ...prev }
                              delete newFilters[index]
                              return newFilters
                            })
                            setSearchMaterial(material.material || '')
                          }
                        }}
                        disabled={material.isAbated}
                      />
                      {editingMaterialIndex === index &&
                        materialOptions.length > 0 && (
                          <DropdownList>
                            {materialOptions.map((option) => (
                              <DropdownItem
                                key={option.id}
                                onClick={() => selectItem('materials', index, option)}
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                              >
                                <span style={{ fontWeight: 'bold' }}>{option.code || option.id}</span>
                                <span style={{ fontSize: '0.7em', color: '#666' }}>
                                  {option.name || option.description}
                                </span>
                              </DropdownItem>
                            ))}
                          </DropdownList>
                        )}
                    </StyledFormGroup>
                    <StyledFormGroup>
                      <label>Un. de medida</label>
                      <select
                        value={material.unitOfMeasure}
                        onChange={(e) =>
                          updateItem(
                            'materials',
                            material.id,
                            'unitOfMeasure',
                            e.target.value,
                          )
                        }
                        disabled={material.isAbated}
                      >
                        <option value='KG'>KG</option>
                        <option value='MM'>MM</option>
                      </select>
                    </StyledFormGroup>
                    <StyledFormGroup>
                      <label>
                        Quantidade - {' '}
                        <span style={{ color: '#0090D3', fontWeight: 'bold' }}>
                          {material.stockBalance}
                          {typedQuantity > 0 && ` - ${futureStock.toFixed(2)} ${currentUnit}`}
                        </span>
                      </label>
                      <input
                        type='number'
                        step='any'
                        autoComplete='off'
                        min='1'
                        value={material.quantityToUse}
                        onChange={(e) =>
                          updateItem(
                            'materials',
                            material.id,
                            'quantityToUse',
                            e.target.value.replace(/[^\d.]/g, ''),
                          )
                        }
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        disabled={material.isAbated}
                        style={{
                          borderColor: material.hasInsufficientStock ? 'red' : undefined,
                          borderWidth: material.hasInsufficientStock ? '2px' : undefined,
                        }}
                      />
                    </StyledFormGroup>
                  </MaterialFields>
                  <InlineDeleteButton
                    type='button'
                    onClick={() => removeItem('materials', material.id)}
                    disabled={material.isAbated}
                  >
                    <img src={trash} alt='Excluir' />
                  </InlineDeleteButton>
                </MaterialItemContainer>
              )
            })}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addMaterial}
                disabled={!canAddMaterial}
              >
                <img src={plus} alt='Adicionar' />
                {canAddMaterial ? 'Adicionar material' : materialMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Insumos</CardTitle>
            </StyledCardHeader>

            {form.resources.map((resource, index) => (
              <MaterialItemContainer key={resource.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Insumo</label>
                    <input
                      type='text'
                      value={
                        editingResourceIndex === index
                          ? searchResource
                          : resource.resource
                      }
                      onChange={(e) => {
                        setSearchResource(e.target.value)
                        setEditingResourceIndex(index)
                      }}
                      onFocus={() => {
                        setEditingResourceIndex(index)
                        setSearchResource(resource.resource)
                      }}
                      disabled={resource.isAbated}
                    />
                    {editingResourceIndex === index &&
                      resourceOptions.length > 0 && (
                        <DropdownList>
                          {resourceOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('resources', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Un. de medida</label>
                    <select
                      value={resource.unit}
                      onChange={(e) =>
                        updateItem(
                          'resources',
                          resource.id,
                          'unit',
                          e.target.value,
                        )
                      }
                      disabled={resource.isAbated}
                    >
                      <option value='KG'>KG</option>
                      <option value='G'>G</option>
                      <option value='UN'>UN</option>
                      <option value='MM'>MM</option>
                      <option value='CM'>CM</option>
                      <option value='M'>M</option>
                    </select>
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type='number'
                      min='0'
                      step={resource.unit === 'UN' ? '1' : '0.01'}
                      value={resource.quantity}
                      onChange={(e) => {
                        let value = e.target.value
                        if (resource.unit === 'UN' && value.includes('.')) {
                          value = value.split('.')[0]
                        }
                        updateItem(
                          'resources',
                          resource.id,
                          'quantity',
                          value,
                        )
                      }}
                      disabled={resource.isAbated}
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('resources', resource.id)}
                  disabled={resource.isAbated}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addResource}
                disabled={!canAddResource}
              >
                <img src={plus} alt='Adicionar' />
                {canAddResource ? 'Adicionar insumo' : resourceMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Serviços</CardTitle>
              <HeaderActions>
                <HeaderIconButton
                  type='button'
                  onClick={copyServicesToClipboard}
                  disabled={form.services.length === 0}
                  title='Copiar serviços para a área de transferência'
                  aria-label='Copiar serviços'
                >
                  <Copy size={18} />
                </HeaderIconButton>
                <HeaderIconButton
                  type='button'
                  onClick={pasteServicesFromClipboard}
                  title='Colar serviços da área de transferência'
                  aria-label='Colar serviços'
                >
                  <ClipboardPaste size={18} />
                </HeaderIconButton>
              </HeaderActions>
            </StyledCardHeader>

            <DndContext
              sensors={serviceSensors}
              collisionDetection={serviceStageCollisionDetection}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={handleServiceDragStart}
              onDragEnd={handleServiceDragEnd}
              onDragCancel={handleServiceDragCancel}
            >
              <SortableContext
                items={form.services.map((s) => `root:${s.rowKey}`)}
                strategy={verticalListSortingStrategy}
              >
            {form.services.map((service, parentIndex) => (
              <React.Fragment key={service.rowKey}>
                <SortableServiceCard
                  service={service}
                  parentIndex={parentIndex}
                  isExpanded={expandedServiceRowKeys.has(service.rowKey)}
                  onToggleExpand={() => toggleServiceExpand(service.rowKey)}
                  onRemove={() => removeService(service.rowKey)}
                  isEditing={
                    editingServiceIndex === parentIndex &&
                    editingServiceChildIndex === null
                  }
                  searchService={searchService}
                  serviceOptions={serviceOptions}
                  setSearchService={setSearchService}
                  onStartEdit={() => {
                    setEditingServiceIndex(parentIndex)
                    setEditingServiceChildIndex(null)
                  }}
                  selectItem={(option) =>
                    selectItem('services', parentIndex, option)
                  }
                  updateItem={(field, value) =>
                    updateItem('services', service.rowKey, field, value)
                  }
                  setFeedback={setFeedback}
                  addServiceFiles={(files) => addServiceFiles(service.rowKey, files)}
                  downloadPendingServiceFile={downloadPendingServiceFile}
                  removePendingServiceFile={(fileIndex) =>
                    removePendingServiceFile(service.rowKey, fileIndex)
                  }
                  downloadExistingServiceFile={downloadExistingServiceFile}
                  removeExistingServiceFile={removeExistingServiceFile}
                  sortable
                  canAddChild={canAddChild(service)}
                  onAddChild={() => addChildService(parentIndex)}
                  onMoveUp={() =>
                    setForm((prev) => ({
                      ...prev,
                      services: reorderParentServices(
                        prev.services,
                        parentIndex,
                        'up',
                      ),
                    }))
                  }
                  onMoveDown={() =>
                    setForm((prev) => ({
                      ...prev,
                      services: reorderParentServices(
                        prev.services,
                        parentIndex,
                        'down',
                      ),
                    }))
                  }
                  siblingCount={form.services.length}
                />
                <SortableContext
                  items={(service.children ?? []).map(
                    (c) => `child:${service.rowKey}:${c.rowKey}`,
                  )}
                  strategy={verticalListSortingStrategy}
                >
                {(service.children ?? []).map((child, childIndex) => (
                  <SortableServiceCard
                    key={child.rowKey}
                    service={child}
                    parentIndex={parentIndex}
                    parentRowKey={service.rowKey}
                    childIndex={childIndex}
                    isChild
                    sortable
                    isExpanded={expandedServiceRowKeys.has(child.rowKey)}
                    onToggleExpand={() => toggleServiceExpand(child.rowKey)}
                    onRemove={() => removeService(child.rowKey)}
                    isEditing={
                      editingServiceIndex === parentIndex &&
                      editingServiceChildIndex === childIndex
                    }
                    searchService={searchService}
                    serviceOptions={serviceOptions}
                    setSearchService={setSearchService}
                    onStartEdit={() => {
                      setEditingServiceIndex(parentIndex)
                      setEditingServiceChildIndex(childIndex)
                    }}
                    selectItem={(option) =>
                      selectItem('services', parentIndex, option, childIndex)
                    }
                    updateItem={(field, value) =>
                      updateItem('services', child.rowKey, field, value)
                    }
                    setFeedback={setFeedback}
                    addServiceFiles={(files) => addServiceFiles(child.rowKey, files)}
                    downloadPendingServiceFile={downloadPendingServiceFile}
                    removePendingServiceFile={(fileIndex) =>
                      removePendingServiceFile(child.rowKey, fileIndex)
                    }
                    downloadExistingServiceFile={downloadExistingServiceFile}
                    removeExistingServiceFile={removeExistingServiceFile}
                    onMoveUp={() =>
                      setForm((prev) => ({
                        ...prev,
                        services: reorderChildServices(
                          prev.services,
                          parentIndex,
                          childIndex,
                          'up',
                        ),
                      }))
                    }
                    onMoveDown={() =>
                      setForm((prev) => ({
                        ...prev,
                        services: reorderChildServices(
                          prev.services,
                          parentIndex,
                          childIndex,
                          'down',
                        ),
                      }))
                    }
                    siblingCount={service.children?.length ?? 0}
                  />
                ))}
                </SortableContext>
              </React.Fragment>
            ))}
              </SortableContext>
              <DragOverlay>
                {activeServiceDragLabel ? (
                  <ServiceStageDragOverlay label={activeServiceDragLabel} />
                ) : null}
              </DragOverlay>
            </DndContext>

            <SectionButtons>
              <AddButton type='button' onClick={addService}>
                <img src={plus} alt='Adicionar' />
                Adicionar serviço
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <ActionButtons
            style={{ marginTop: '24px', justifyContent: 'flex-end' }}
          >
            <CancelButton type='button' onClick={handleCancel}>
              Cancelar
            </CancelButton>
            <SaveButton
              type='button'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <img src={save} alt='Salvar' />
              {isSubmitting
                ? isEditMode
                  ? 'Atualizando...'
                  : 'Criando OP...'
                : isEditMode
                  ? 'Atualizar OP'
                  : 'Salvar OP'}
            </SaveButton>
          </ActionButtons>
        </form>
      </MainContainer>
      {showSubOpModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '8px',
              width: '100%',
              maxWidth: '720px',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: '20px',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Árvore de OPs detectada</h3>
            <p style={{ marginTop: 0, color: '#666' }}>
              Foram detectadas OPs filhas para este script. Confirme para gerá-las agora.
            </p>
            {subOpPreview.length === 0 ? (
              <p style={{ color: '#666' }}>Nenhuma OP filha detectada para o script selecionado.</p>
            ) : (
              <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
                {subOpPreview.map((node) => (
                  <div
                    key={node.tempId}
                    style={{
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      marginLeft: `${(node.level - 1) * 16}px`,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{node.codeOfOP}</div>
                    <div>{node.productName}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Quantidade calculada: {node.calculatedQuantity}
                    </div>
                    <div style={{ fontSize: '13px', color: '#444', marginTop: '6px' }}>
                      Script: {subOpScriptDetails[node.scriptId]?.scriptName || `#${node.scriptId}`}
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Serviços</div>
                      {subOpScriptDetails[node.scriptId]?.services?.length ? (
                        <div style={{ display: 'grid', gap: '4px' }}>
                          {subOpScriptDetails[node.scriptId].services.map((service) => (
                            <div key={`${node.tempId}_${service.id}`} style={{ fontSize: '13px' }}>
                              <strong>{service.name}</strong>
                              {service.description ? ` - ${service.description}` : ''}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#777' }}>Nenhum serviço no script.</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <CancelButton
                type='button'
                onClick={handleCancelSubOpModal}
                disabled={isSubmitting}
              >
                Cancelar
              </CancelButton>
              <SaveButton
                type='button'
                onClick={() => void handleConfirmSubOpModal()}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Gerando...' : 'Gerar OPs filhas'}
              </SaveButton>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CreateOP
